/**
 * iter1037 — BHI Pathway Adherence Panel (feature #4).
 *
 * Renders a horizontal bar-list of active pathways with 7-day and 30-day
 * execution adherence percentages, sourced from `/api/bhi/adherence`.
 * Non-gamified: pure informational, no streaks or scores.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Activity, Flame, TrendingUp, Loader2 } from "lucide-react";
import { useReadingLevel } from "@/contexts/BhiReadingLevelContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_COLOUR = (pct) => {
  if (pct >= 70) return { bar: "from-emerald-500 to-emerald-400", chip: "text-emerald-600 dark:text-emerald-400" };
  if (pct >= 40) return { bar: "from-amber-500 to-amber-400",     chip: "text-amber-600 dark:text-amber-400" };
  if (pct >= 15) return { bar: "from-orange-500 to-orange-400",   chip: "text-orange-600 dark:text-orange-400" };
  return               { bar: "from-slate-400 to-slate-300",     chip: "text-slate-500" };
};

export default function BhiPathwayAdherencePanel({ isDark = false }) {
  const [state, setState] = useState({ loading: true, data: null });
  const { isPlain } = useReadingLevel();

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/bhi/adherence`)
      .then(({ data }) => { if (!cancelled) setState({ loading: false, data }); })
      .catch(() => { if (!cancelled) setState({ loading: false, data: null }); });
    return () => { cancelled = true; };
  }, []);

  if (state.loading) {
    return (
      <div className={`rounded-2xl border p-4 ${isDark ? "bg-slate-900/50 border-slate-800" : "bg-white border-slate-200"}`}>
        <Loader2 className="w-4 h-4 animate-spin opacity-60" />
      </div>
    );
  }
  if (!state.data?.pathways?.length) return null;

  const total7 = state.data.total_done_7d || 0;

  return (
    <div
      className={`rounded-2xl border overflow-hidden ${isDark ? "bg-slate-900/50 border-slate-800" : "bg-white border-slate-200"}`}
      data-testid="bhi-adherence-panel"
    >
      {/* Header */}
      <div className={`p-3 sm:p-4 flex items-start gap-3 border-b ${isDark ? "border-slate-800" : "border-slate-100"}`}>
        <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-sky-500/15 text-sky-500 flex items-center justify-center shrink-0">
          <Activity className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold leading-tight">
            {isPlain ? "Колко редовно правя препоръките" : "Изпълнение по pathways · 7 & 30 дни"}
          </h3>
          <p className={`text-[11px] mt-0.5 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
            {isPlain
              ? `Последната седмица: ${total7} отметки. Всяка отметка модулира съответния биологичен път.`
              : `Последна седмица: ${total7} записа в daily execution log.`}
          </p>
        </div>
        <TrendingUp className={`hidden sm:block w-4 h-4 shrink-0 ${isDark ? "text-slate-400" : "text-slate-500"}`} />
      </div>

      {/* Rows */}
      <div className="divide-y divide-slate-200 dark:divide-slate-800">
        {state.data.pathways.map((p) => {
          const tint7  = TIER_COLOUR(p.percent_7d);
          const tint30 = TIER_COLOUR(p.percent_30d);
          return (
            <div key={p.pathway_anchor} className="p-3 sm:p-4"
                 data-testid={`bhi-adherence-row-${p.pathway_anchor}`}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-1.5 gap-1 sm:gap-3">
                <div className="text-sm font-semibold truncate">{p.label_bg}</div>
                <div className="flex items-center gap-2 shrink-0 text-[11px] font-semibold">
                  <span className={tint7.chip}>7д · {p.percent_7d.toFixed(0)}%</span>
                  <span className={`opacity-40`}>·</span>
                  <span className={tint30.chip}>30д · {p.percent_30d.toFixed(0)}%</span>
                </div>
              </div>
              {/* Dual progress bar */}
              <div className="relative h-2 rounded-full overflow-hidden bg-slate-200/60 dark:bg-slate-800">
                <div
                  className={`absolute inset-y-0 left-0 rounded-full bg-gradient-to-r ${tint30.bar} opacity-40`}
                  style={{ width: `${Math.min(p.percent_30d, 100)}%` }}
                  title={`30 дни: ${p.percent_30d.toFixed(0)}%`}
                />
                <div
                  className={`absolute inset-y-0 left-0 rounded-full bg-gradient-to-r ${tint7.bar}`}
                  style={{ width: `${Math.min(p.percent_7d, 100)}%` }}
                  title={`7 дни: ${p.percent_7d.toFixed(0)}%`}
                />
              </div>
              <div className={`mt-1 text-[10px] ${isDark ? "text-slate-500" : "text-slate-400"}`}>
                {p.done_7d} отметки за 7д · {p.done_30d} за 30д · цел ≥ 1/ден
              </div>
            </div>
          );
        })}
      </div>

      {total7 === 0 && (
        <div className={`px-4 py-3 text-xs ${isDark ? "bg-amber-900/20 text-amber-300" : "bg-amber-50 text-amber-700"}`}>
          <Flame className="w-3.5 h-3.5 inline mr-1" />
          {isPlain
            ? "Няма отметки в последната седмица. Отбележи първо действие горе с „Направих го“."
            : "Няма записи в execution log-а за последната седмица. Използвайте „Направих го“ горе или ежедневния навик карта."}
        </div>
      )}
    </div>
  );
}
